"use client";

import { useCallback } from "react";
import { useAtom, type WritableAtom } from "jotai";
import { RESET } from "jotai/utils";
import { v4 as uuidv4 } from "uuid";
import { hotelBookingKeyAtom } from "@/stores/hotel-checkout";
import { rentalBookingKeyAtom } from "@/stores/rental-checkout";
import { SvcType } from "@/lib/utils";
import {
  BookingKeyContext,
  buildBookingContextKey,
} from "@/hooks/booking-checkout/booking.checkout.helpers";
import { tourActBookingKeyAtom } from "@/stores/tour-act-checkout";

type BookingKeyContextMap = {
  hotel: BookingKeyContext;
  rental: BookingKeyContext;
  tour_act: BookingKeyContext;
  spa_msg: BookingKeyContext;
  any: any;
};

type BookingKeyState<T> = {
  contextKey: string;
  key: string;
} & T;

type BookingServiceConfig<T> = {
  atom: WritableAtom<any, [any | typeof RESET], void>;
  buildContextKey: (context: T) => string | null;
};

const BOOKING_SERVICE_CONFIG: {
  [K in SvcType]: BookingServiceConfig<BookingKeyContextMap[K]>;
} = {
  hotel: {
    atom: hotelBookingKeyAtom,
    buildContextKey: buildBookingContextKey,
  },
  rental: {
    atom: rentalBookingKeyAtom,
    buildContextKey: buildBookingContextKey,
  },
  tour_act: {
    atom: tourActBookingKeyAtom,
    buildContextKey: buildBookingContextKey,
  },
  spa_msg: {
    atom: rentalBookingKeyAtom,
    buildContextKey: buildBookingContextKey,
  },
};

/**
 * Manage the reusable key for a booking context, shared across services.
 */
export const useBookingKey = <T extends SvcType>(serviceType: T) => {
  const config = BOOKING_SERVICE_CONFIG[serviceType];
  const [bookingKey, setBookingKey] = useAtom(
    config.atom as WritableAtom<
      BookingKeyState<BookingKeyContextMap[T]> | null,
      [BookingKeyState<BookingKeyContextMap[T]> | null | typeof RESET],
      void
    >,
  );

  const getOrCreateKey = useCallback(
    (context: BookingKeyContextMap[T]) => {
      const contextKey = config.buildContextKey(context);
      if (!contextKey) {
        return null;
      }

      const key =
        bookingKey?.contextKey === contextKey ? bookingKey.key : uuidv4();
      setBookingKey({ ...context, contextKey, key });
      return key;
    },
    [bookingKey, config, setBookingKey],
  );

  const rotateKey = useCallback(
    (context: BookingKeyContextMap[T]) => {
      const contextKey = config.buildContextKey(context);
      if (!contextKey) {
        return null;
      }

      const key = uuidv4();
      setBookingKey({ ...context, contextKey, key });
      return key;
    },
    [config, setBookingKey],
  );

  const clearKey = useCallback(() => {
    setBookingKey(RESET);
  }, [setBookingKey]);

  return {
    bookingKey,
    getOrCreateKey,
    rotateKey,
    clearKey,
  };
};
